/* eslint-disable no-await-in-loop */
/* eslint-disable @typescript-eslint/naming-convention */
import RemoveItemModal from '@brands/Dashboard/Dashboard/Components/RemoveItemModal/RemoveItemModal';
import UnsavedChangesModal from '@brands/Dashboard/Dashboard/Components/UnsavedChangesModal/UnsavedChangesModal';
import {
  convertInsuranceToInsuranceForm,
  updateDataInsuranceNewDesign,
  updateDataSecondaryInsuranceNewDesign,
  UploadedFile,
} from '@brands/Dashboard/MyProfile/Patient/utils/utilsFunctions';
import { useWindowSize } from '@brands/hooks';
import useRoleNavigate from '@brands/hooks/useRoleNavigate';
import { useSnackbar } from '@brands/hooks/useStackbar';
import { useUnsavedChangesWarning } from '@brands/hooks/useUnsavedChangesWarning';
import { RegisterMediaPayload } from '@brands/services/communication/registersConversationMedia';
import { createPatientInsurance } from '@brands/services/identity/createPatientInsurance';
import { deactivateInsuranceById } from '@brands/services/identity/deactivatePatientInsurance';
import { getPatientInsurance } from '@brands/services/identity/getPatientInsurance';
import { Insurance as InsuranceType, InsuranceMedia, UserProfile } from '@brands/services/identity/types/UserProfile';
import { confirmOneReportMedia } from '@brands/services/oneReport/confirmOneReportMedia';
import { getOneReportMedia } from '@brands/services/oneReport/getOneReportMedia';
import { registerOneReportMedia } from '@brands/services/oneReport/registerOneReportMedia';
import { selectAuth } from '@brands/store/selectors/auth';
import { base64ListToFileList } from '@brands/Utils/base64ToFile';
import { displayErrorDetails } from '@brands/Utils/displayError';
import GenericSnackbar from '@brands/Utils/GenericSnackbar';
import { getFileMimeType } from '@brands/Utils/truncateString';
import { yupResolver } from '@hookform/resolvers/yup';
import { heicTo } from 'heic-to';
import { isEqual } from 'lodash';
import React, { useEffect, useRef, useState } from 'react';
import { useForm } from 'react-hook-form';
import { FiMinus, FiPlus } from 'react-icons/fi';

import Container from '../../Components/Container/Container';
import MuiToggle from '../../Components/Inputs/MuiToggle/MuiToggle';
import { Loading } from '../../Components/LoadingSpinner/Loading';
import { useAppSelector } from '../../hooks/useReduxHook';
import ProfileCommonHeader from '../MyProfile/Patient/Sections/ProfileCommonHeader/ProfileCommonHeader';
import InsuranceFormSection from './InsuranceFormSection';
import styles from './styles.module.scss';
import { defaultInsuranceForm, InsuranceForm, InsuranceImages, InsuranceMediaContent } from './utils/types';
import { InsuranceTab, validationSchemaNewDesign } from './utils/validationSchema';

const Insurance = (): JSX.Element => {
  const screenSize = useWindowSize();
  const phoneInputRef = useRef<HTMLInputElement>(null);
  const roleNavigate = useRoleNavigate();
  const formRef = useRef<HTMLFormElement | null>(null);
  const [allFilled, setAllFilled] = useState(false);
  const [render, setRender] = useState(true);
  const { userInfo } = useAppSelector(selectAuth);
  const [isDragging, setIsDragging] = useState(false);
  const [patientInsurance, setPatientInsurance] = useState<InsuranceType[]>([]);
  const { snackbar, showSnackbar, closeSnackbar } = useSnackbar();
  const [currentTab, setCurrentTab] = useState('primary_insurance');
  const [removeItemModal, setRemoveItemModal] = useState(false);
  const [images, setImages] = useState<InsuranceImages>({
    front_of_card: [],
    back_of_card: [],
    secondary_front_of_card: [],
    secondary_back_of_card: [],
  });
  const [initialValues, setInitialValues] = useState<InsuranceForm>(defaultInsuranceForm);
  const {
    register,
    handleSubmit,
    setValue,
    watch,
    reset,
    getValues,
    formState: { errors },
  } = useForm<InsuranceForm>({
    mode: 'onChange',
    resolver: yupResolver(validationSchemaNewDesign(currentTab as InsuranceTab)),
    defaultValues: initialValues,
  });

  const { showModal, confirmNavigation, saveChanges, hideModal, handleBlockedNavigation } = useUnsavedChangesWarning(
    allFilled,
    formRef,
    reset,
  );

  useEffect(() => {
    if (initialValues) {
      setInitialValues(initialValues);
      reset(initialValues);
    }
  }, [initialValues, reset]);

  const myInsuranceTabs = [
    { label: 'Primary insurance', value: 'primary_insurance' },
    { label: 'Secondary insurance', value: 'secondary_insurance' },
  ];

  const uploadMedia = async (imagesValue: UploadedFile[]): Promise<string[]> => {
    let imageList: string[] = [];
    const newUploadedImages = imagesValue.filter((image) => image.dataURL.startsWith('data:'));
    const existingImages = imagesValue.filter((image) => image.dataURL.startsWith('https://'));

    if (Array.isArray(newUploadedImages)) {
      imageList = newUploadedImages.map((image) => {
        if (typeof image === 'string') {
          return image;
        }
        return image.dataURL;
      });
    }
    const fileList = await base64ListToFileList(imageList || []);

    const mediaIDs: string[] = [];

    if (Array.isArray(newUploadedImages) && Array.isArray(fileList)) {
      await Promise.all(
        Array.from(fileList).map(async (file, index) => {
          const image = newUploadedImages[index] as UploadedFile;
          const mimeType = getFileMimeType(image.name);
          const payload: RegisterMediaPayload = {
            files: [
              {
                filename: image.name,
                mimetype: mimeType,
              },
            ],
          };
          try {
            const registeredMedia = await registerOneReportMedia(payload);
            const mediaId = registeredMedia.files[0].upload_id;
            await fetch(registeredMedia.files[0].upload_url, {
              method: 'PUT',
              body: file,
              headers: { 'content-type': mimeType },
            });
            await confirmOneReportMedia(mediaId);
            mediaIDs.push(mediaId);
          } catch (error: unknown) {
            displayErrorDetails(error);
          }
        }),
      );
    }

    if (existingImages.length > 0) {
      const mediaIDsFromExistingImages = existingImages.map((image) => image.media_id);
      mediaIDs.push(...(mediaIDsFromExistingImages as string[]));
    }
    return mediaIDs;
  };

  const mapMediaToInsuranceMedia = (media: string[], type: string): InsuranceMedia[] => {
    return media.map((mediaId) => ({ media_id: mediaId, type }));
  };

  const onSubmit = async (data: InsuranceForm): Promise<void> => {
    setRender(false);

    const insuranceArray: InsuranceType[] = [];

    /** =======================
     * PRIMARY INSURANCE
     ======================= */
    if (currentTab === 'primary_insurance') {
      let front_of_card_media_ids: string[] = [];
      let back_of_card_media_ids: string[] = [];

      await Promise.all([
        (front_of_card_media_ids = await uploadMedia(data.front_of_card)),
        (back_of_card_media_ids = await uploadMedia(data.back_of_card)),
      ]);

      const primaryInsurance = updateDataInsuranceNewDesign(data);

      primaryInsurance.front_of_card = mapMediaToInsuranceMedia(front_of_card_media_ids, 'front_of_card');
      primaryInsurance.back_of_card = mapMediaToInsuranceMedia(back_of_card_media_ids, 'back_of_card');

      insuranceArray.push(primaryInsurance);
    }

    /** =======================
     * SECONDARY INSURANCE
     ======================= */
    if (currentTab === 'secondary_insurance') {
      let secondary_front_of_card_media_ids: string[] = [];
      let secondary_back_of_card_media_ids: string[] = [];

      await Promise.all([
        (secondary_front_of_card_media_ids = await uploadMedia(data.secondary_front_of_card)),
        (secondary_back_of_card_media_ids = await uploadMedia(data.secondary_back_of_card)),
      ]);

      const secondaryInsurance = updateDataSecondaryInsuranceNewDesign(data);

      secondaryInsurance.front_of_card = mapMediaToInsuranceMedia(secondary_front_of_card_media_ids, 'front_of_card');
      secondaryInsurance.back_of_card = mapMediaToInsuranceMedia(secondary_back_of_card_media_ids, 'back_of_card');

      secondaryInsurance.is_secondary = true;

      insuranceArray.push(secondaryInsurance);
    }

    /** =======================
     * SUBMIT
     ======================= */
    const createdInsurance = await createPatientInsurance({
      user_id: userInfo.id,
      insurance: insuranceArray,
    });

    const newInsurance = createdInsurance[0];

    setInitialValues(structuredClone(data));

    setPatientInsurance((prev) => {
      const isSecondary = newInsurance.is_secondary;

      const index = prev.findIndex((insurance) => insurance.is_secondary === isSecondary);

      if (index !== -1) {
        const updated = [...prev];
        updated[index] = newInsurance;
        return updated;
      }

      return [...prev, newInsurance];
    });

    setRender(true);
    showSnackbar('Your changes have been saved', 'success');
  };

  const onInvalid = (): void => {
    displayErrorDetails('Please fill out all required fields.');
  };

  const fetchUserInsurance = async (): Promise<InsuranceForm[]> => {
    const userInsurance = await getPatientInsurance(userInfo?.id);
    setPatientInsurance(userInsurance);
    const mappedInsurance = userInsurance.map((insurance) => {
      const mappedInsuranceData = convertInsuranceToInsuranceForm(insurance, userInfo as UserProfile);
      return mappedInsuranceData;
    });
    return mappedInsurance;
  };

  const hasPrimaryInsurance = patientInsurance.some((i) => !i.is_secondary);
  const hasSecondaryInsurance = patientInsurance.some((i) => i.is_secondary);

  const loadImages = async (media_list: InsuranceMedia[]): Promise<InsuranceMediaContent[]> => {
    const imagePromises = media_list.map(async (mediaOneReport: InsuranceMedia) => {
      const mediaData = await getOneReportMedia(mediaOneReport.media_id!);
      return {
        src: mediaData.url,
        width: 4,
        height: 3,
        type: mediaOneReport.type,
        dataURL: mediaData.url,
        media_id: mediaOneReport.media_id,
        name: mediaData.filename,
      } as InsuranceMediaContent;
    });
    const imagesList = await Promise.all(imagePromises);
    return imagesList;
  };

  const handleTabChange = (nextTab: string): void => {
    if (nextTab === currentTab) return;
    handleBlockedNavigation(() => {
      setCurrentTab(nextTab);
      window.history.replaceState(null, '', `#${nextTab}`);
    });
  };

  useEffect(() => {
    (async (): Promise<void> => {
      const hash = window.location.hash.replace('#', '');

      if (myInsuranceTabs.some((t) => t.value === hash)) {
        setCurrentTab(hash);
      }

      const mappedInsurance = await fetchUserInsurance();

      if (!mappedInsurance.length) return;

      const primaryInsurance = mappedInsurance.find((i) => !i.is_secondary);
      const secondaryInsurance = mappedInsurance.find((i) => i.is_secondary);

      const imagesObj: InsuranceImages = {
        front_of_card: [],
        back_of_card: [],
        secondary_front_of_card: [],
        secondary_back_of_card: [],
      };

      const loadInsuranceImages = async (media?: InsuranceMedia[]) => {
        if (!media?.length) {
          return { front: [], back: [] };
        }

        const filtered = media.filter((m) => m.type === 'front_of_card' || m.type === 'back_of_card');

        const loaded = await loadImages(filtered);

        return {
          front: loaded.filter((i) => i.type === 'front_of_card'),
          back: loaded.filter((i) => i.type === 'back_of_card'),
        };
      };

      /** =======================
       * PRIMARY INSURANCE
       ======================= */
      if (primaryInsurance) {
        const { media, ...rest } = primaryInsurance;

        Object.entries(rest).forEach(([key, value]) => {
          setValue(key as keyof InsuranceForm, value as any);
        });

        const { front, back } = await loadInsuranceImages(media);
        imagesObj.front_of_card = front;
        imagesObj.back_of_card = back;
      }

      /** =======================
       * SECONDARY INSURANCE
       ======================= */
      if (secondaryInsurance) {
        const { media, ...rest } = secondaryInsurance;

        Object.entries(rest).forEach(([key, value]) => {
          setValue(`secondary_${key}` as keyof InsuranceForm, value as any);
        });

        const { front, back } = await loadInsuranceImages(media);
        imagesObj.secondary_front_of_card = front;
        imagesObj.secondary_back_of_card = back;
      }

      /** =======================
       * FINALIZE
       ======================= */
      setImages(imagesObj);

      setValue('front_of_card', imagesObj.front_of_card);
      setValue('back_of_card', imagesObj.back_of_card);
      setValue('secondary_front_of_card', imagesObj.secondary_front_of_card);
      setValue('secondary_back_of_card', imagesObj.secondary_back_of_card);
      setInitialValues(getValues());
    })();
  }, []);

  const addImages = (files: File[], type: keyof InsuranceImages): void => {
    const newImages = files.filter(
      (file) =>
        file.type.startsWith('image/') ||
        file.type === 'application/pdf' ||
        file.type === 'application/msword' ||
        file.type === 'application/vnd.openxmlformats-officedocument.wordprocessingml.document' ||
        file.type === 'application/vnd.ms-powerpoint' ||
        file.type === 'application/vnd.openxmlformats-officedocument.presentationml.presentation',
    );
    if (getValues(type).length + files.length > 10) {
      displayErrorDetails('Max 10 files are allowed to upload.');
      return;
    }
    const imagesArray: UploadedFile[] = [];
    for (let i = 0; i < newImages.length; i++) {
      const file = newImages[i];
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.addEventListener('load', () => {
        imagesArray.push({ name: file.name, dataURL: reader.result as string });
        if (imagesArray.length === newImages.length) {
          setValue(type, imagesArray, { shouldValidate: true });
          setImages({
            ...images,
            [type]: imagesArray,
          });
        }
      });
    }
  };

  const deleteMedia = (mediaName: string, type: keyof InsuranceImages): void => {
    const selectedMedia = getValues(type).find((image) => image.name === mediaName);
    if (selectedMedia) {
      setValue(
        type,
        getValues(type).filter((image) => image.name !== mediaName),
        { shouldValidate: true },
      );
      setImages({
        ...images,
        [type]: images[type].filter((image) => image.name !== mediaName),
      });
    }
  };

  const handleChange = async (event: React.ChangeEvent<HTMLInputElement>): Promise<void> => {
    const files = Array.from(event.target.files as unknown as File[]);
    const convertedFiles = await Promise.all(
      files.map(async (file) => {
        if (file.type === 'image/heic') {
          try {
            const jpegBlob = await heicTo({
              blob: file,
              type: 'image/jpeg',
              quality: 0.85,
            });

            return new File([jpegBlob], file.name.replace(/\.heic$/i, '.jpeg'), {
              type: 'image/jpeg',
            });
          } catch (error: any) {
            displayErrorDetails(`Error converting HEIC to JPEG: ${error}`);
            return file;
          }
        }

        return file;
      }),
    );

    addImages(convertedFiles, event.target.id as keyof InsuranceImages);
  };

  const handleDragOver = (event: React.DragEvent<HTMLDivElement>): void => {
    event.preventDefault();
    event.stopPropagation();
  };

  const handleDrop = async (event: React.DragEvent<HTMLDivElement>, type: keyof InsuranceImages): Promise<void> => {
    event.preventDefault();
    setIsDragging(false);

    if (!event.dataTransfer.files?.length) return;
    let file = event.dataTransfer.files[0];

    if (file.type === 'image/heic') {
      try {
        const jpegBlob = await heicTo({
          blob: file,
          type: 'image/jpeg',
          quality: 0.85,
        });
        file = new File([jpegBlob], file.name.replace(/\.heic$/i, '.jpeg'), {
          type: 'image/jpeg',
        });
      } catch (error: any) {
        displayErrorDetails(`Error converting HEIC to JPEG: ${error}`);
      }
    }

    addImages([file], type);
  };

  const handleDragEnter = (event: React.DragEvent<HTMLDivElement>): void => {
    event.preventDefault();
    event.stopPropagation();
    setIsDragging(true);
  };

  const handleDragLeave = (event: React.DragEvent<HTMLDivElement>): void => {
    event.preventDefault();
    event.stopPropagation();
    setIsDragging(false);
  };

  // const requiredInsuranceFields = [
  //   'insurance_carrier_id',
  //   'member_id',
  //   'insurance_phone_number',
  //   'insurance_address',
  //   'insurance_city',
  //   'insurance_state',
  //   'insurance_zip_code',
  //   'insurance_through',
  //   'state_funded_coverage_status',
  // ];

  // const requiredInsuranceFieldsInsuranceThrough = [
  //   'insurance_first_name',
  //   'insurance_last_name',
  //   'insurance_date_of_birth',
  //   'insurance_gender',
  //   'insurance_address1',
  //   'insurance_city',
  //   'insurance_state',
  //   'insurance_zip_code',
  // ];

  // const checkInsuranceSet = (prefix: string): boolean => {
  //   const isFilled = (v: unknown) => v !== undefined && v !== null && !(typeof v === 'string' && v.trim() === '');

  //   const baseFilled = requiredInsuranceFields.every((field) =>
  //     isFilled(getValues(`${prefix}${field}` as keyof InsuranceForm)),
  //   );

  //   const insuranceThroughValue = getValues(`${prefix}insurance_through` as keyof InsuranceForm);

  //   const dependentFilled =
  //     insuranceThroughValue && insuranceThroughValue !== 'myself'
  //       ? requiredInsuranceFieldsInsuranceThrough.every((field) =>
  //           isFilled(getValues(`${prefix}${field}` as keyof InsuranceForm)),
  //         )
  //       : true;

  //   return baseFilled && dependentFilled;
  // };

  const removeInsurance = async (insuranceId: number, isSecondary: boolean): Promise<void> => {
    try {
      await deactivateInsuranceById(insuranceId);

      const prefix = isSecondary ? 'secondary_' : '';

      // Clear form fields
      Object.keys(getValues()).forEach((key) => {
        const isSecondaryField = key.startsWith('secondary_');

        if (isSecondary ? isSecondaryField : !isSecondaryField) {
          setValue(key as keyof InsuranceForm, undefined);
        }
      });

      // Clear images
      setImages((prev) => ({
        ...prev,
        ...(isSecondary
          ? {
              secondary_front_of_card: [],
              secondary_back_of_card: [],
            }
          : {
              front_of_card: [],
              back_of_card: [],
            }),
      }));

      const userInsurance = await getPatientInsurance(userInfo?.id);
      setPatientInsurance(userInsurance);
      setInitialValues(getValues());
    } catch (error: unknown) {
      displayErrorDetails(error);
    }
  };

  useEffect(() => {
    const normalizeValue = (value: unknown) => {
      if (value === undefined || value === null || value === '') {
        return null;
      }
      return value;
    };

    const normalizeObject = (values: Partial<InsuranceForm>): Partial<InsuranceForm> => {
      return Object.keys(values).reduce((acc, key) => {
        const value = values[key as keyof InsuranceForm];

        const normalized = Array.isArray(value) ? value : normalizeValue(value);

        if (
          normalized === undefined ||
          normalized === null ||
          (Array.isArray(normalized) && normalized.length === 0) ||
          normalized === ''
        ) {
          return acc;
        }

        acc[key as keyof InsuranceForm] = normalized;
        return acc;
      }, {} as Partial<InsuranceForm>);
    };

    const filterByTab = (values: InsuranceForm, isSecondary: boolean): Partial<InsuranceForm> => {
      return Object.keys(values).reduce((acc, key) => {
        const isSecondaryField = key.startsWith('secondary_');

        if (isSecondary && isSecondaryField) {
          acc[key as keyof InsuranceForm] = values[key as keyof InsuranceForm];
        }

        if (!isSecondary && !isSecondaryField) {
          acc[key as keyof InsuranceForm] = values[key as keyof InsuranceForm];
        }

        return acc;
      }, {} as Partial<InsuranceForm>);
    };

    const checkAllFields = (): void => {
      const currentValues = getValues();
      const isSecondaryTab = currentTab === 'secondary_insurance';

      const filteredCurrentValues = normalizeObject(filterByTab(currentValues, isSecondaryTab));

      const filteredInitialValues = normalizeObject(filterByTab(initialValues, isSecondaryTab));

      const formChanged = !isEqual(filteredCurrentValues, filteredInitialValues);

      setAllFilled(formChanged);
    };

    const subscription = watch(() => {
      checkAllFields();
    });

    checkAllFields();

    return () => subscription.unsubscribe();
  }, [watch, currentTab, initialValues]);

  const removeSelectedInsurance = async (): Promise<void> => {
    const insuranceToRemove = patientInsurance.find((insurance) =>
      currentTab === 'secondary_insurance' ? insurance.is_secondary : !insurance.is_secondary,
    );

    if (!insuranceToRemove?.id) return;

    const insurance = patientInsurance.find((i) => i.is_secondary === (currentTab === 'secondary_insurance'));

    if (insurance) {
      removeInsurance(Number(insurance.id), !!insurance.is_secondary);
    }

    setRemoveItemModal(false);
  };

  if (render) {
    return (
      <Container
        patientDashboard
        childClassName={styles.dashboard}
        containerStyle={{
          display: 'flex',
          flexDirection: 'column',
          justifyContent: screenSize.width >= 1289 ? 'center' : 'start',
          overflowY: screenSize.width >= 1289 ? 'hidden' : 'scroll',
        }}
      >
        <div className={styles.myProfileContainer}>
          <ProfileCommonHeader
            currentTab={currentTab}
            handleTabChange={handleTabChange}
            tabs={myInsuranceTabs}
            sx={{
              wrapper: { mt: 2 },
              tabList: { minHeight: 48 },
              tab: { textTransform: 'capitalize' },
            }}
            className={styles.insuranceProfileCommonHeaderDiv}
          />
          <form ref={formRef} className={styles.insuranceContainer} onSubmit={handleSubmit(onSubmit, onInvalid)}>
            <div className={styles.insuranceWrapper}>
              {currentTab === 'primary_insurance' && (
                <InsuranceFormSection
                  isSecondary={false}
                  phoneInputRef={phoneInputRef}
                  register={register}
                  errors={errors}
                  setValue={setValue}
                  watch={watch}
                  getValues={getValues}
                  handleChange={handleChange}
                  handleDragOver={handleDragOver}
                  handleDrop={handleDrop}
                  handleDragEnter={handleDragEnter}
                  handleDragLeave={handleDragLeave}
                  isDragging={isDragging}
                  deleteMedia={deleteMedia}
                  images={images}
                />
              )}

              {currentTab === 'secondary_insurance' && (
                <InsuranceFormSection
                  isSecondary
                  phoneInputRef={phoneInputRef}
                  register={register}
                  errors={errors}
                  setValue={setValue}
                  watch={watch}
                  getValues={getValues}
                  handleChange={handleChange}
                  handleDragOver={handleDragOver}
                  handleDrop={handleDrop}
                  handleDragEnter={handleDragEnter}
                  handleDragLeave={handleDragLeave}
                  isDragging={isDragging}
                  deleteMedia={deleteMedia}
                  images={images}
                />
              )}

              <div className={styles.buttonContainer}>
                <div className={styles.buttonDiv}>
                  <button type="submit" className={styles.saveButton} disabled={!allFilled}>
                    Save changes
                  </button>
                  {((currentTab === 'primary_insurance' && hasPrimaryInsurance) ||
                    (currentTab === 'secondary_insurance' && hasSecondaryInsurance)) && (
                    <button
                      type="button"
                      className={styles.addSecondaryInsuranceButton}
                      onClick={() => setRemoveItemModal(true)}
                    >
                      {currentTab === 'primary_insurance' ? 'Remove primary insurance' : 'Remove secondary insurance'}
                    </button>
                  )}
                </div>
              </div>
            </div>
            {showModal && (
              <UnsavedChangesModal
                isOpen={showModal}
                onConfirm={confirmNavigation}
                saveChange={saveChanges}
                loading={false}
                hideModal={hideModal}
              />
            )}
            {removeItemModal && (
              <RemoveItemModal
                isOpen={removeItemModal}
                toggleModal={setRemoveItemModal}
                itemName="this insurance"
                removeItem={removeSelectedInsurance}
              />
            )}
          </form>
        </div>

        <GenericSnackbar
          open={snackbar.isOpen}
          message={snackbar.message}
          type={snackbar.type}
          onClose={closeSnackbar}
        />
      </Container>
    );
  }
  return <Loading fullScreen />;
};

export default Insurance;
